import { useState } from 'react';
import { Button } from '../ui/Button';
import type { ActionConfig } from '../../config/actions';
import { ConfirmModal } from './ConfirmModal';

const iconMap: Record<string, string> = {
  'check-square': '✓',
  'thumbs-down': '👎',
  'tags': '🏷️',
  'check': '✓',
  'chevron-circle-right': '→',
  'edit': '✏️',
  'trash': '🗑️',
  'plus': '+',
  'envelope': '✉️',
  'phone': '📞',
  'comment': '💬',
  'map-marker': '📍',
  'download': '⬇️',
};

interface ActionButtonProps {
  action: ActionConfig;
  onExecute: () => void;
  isExecuting?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  showLabel?: boolean;
}

export function ActionButton({
  action,
  onExecute,
  isExecuting = false,
  variant = 'secondary',
  size = 'sm',
  showLabel = true,
}: ActionButtonProps) {
  const [showConfirm, setShowConfirm] = useState(false);

  const handleClick = () => {
    if (action.needsConfirmation) {
      setShowConfirm(true);
    } else {
      onExecute();
    }
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    onExecute();
  };

  const icon = iconMap[action.icon] || '•';

  let buttonVariant = variant;
  if (action.type === 'DELETE_RECORD') {
    buttonVariant = 'danger';
  } else if (action.id.includes('complete') || action.id.includes('mark-complete')) {
    buttonVariant = 'primary';
  }

  return (
    <>
      <Button
        variant={buttonVariant}
        size={size}
        onClick={handleClick}
        isLoading={isExecuting}
        disabled={isExecuting}
      >
        <span className="mr-1">{icon}</span>
        {showLabel && action.name}
      </Button>

      {showConfirm && (
        <ConfirmModal
          title={action.name}
          message={action.confirmationMessage || `Are you sure you want to ${action.name.toLowerCase()}?`}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
          confirmLabel={action.name}
          danger={action.type === 'DELETE_RECORD'}
        />
      )}
    </>
  );
}
